import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * Holds the terminal status the current request has to be answered with.
 *
 * A page that cannot be served is not a different address, it is the same address answered differently, so the
 * status lives here rather than in a redirect to an error route. The server reads it back to set the response
 * status and serializes it alongside the rest of the state, which is what lets the browser hydrate against the
 * error the server rendered instead of trying to build the page that was never there.
 *
 * It is left out of the reset that clears every store when there is no session, because the status belongs to the
 * address rather than the visitor. On the server that reset lands between the guard raising a route's status and the
 * render reading it, which would render the routed page under the error's status.
 */
export const useErrorStore = defineStore('error', () => {
    const statusCode = ref(null);
    // Navigation identity is request-local and must not be serialized into the hydrated state.
    let activeRoute;
    return {
        statusCode,
        beginNavigation,
        captureNavigation,
        raise,
        clear
    };

    /**
     * Invalidates effects from earlier navigations, including another visit to the same URL.
     *
     * @param {Object} route - The normalized route supplied by the router guard.
     */
    function beginNavigation(route) {
        activeRoute = route;
    }

    /**
     * Captures the navigation that owns an asynchronous page operation.
     *
     * @param {Object} [route] - The prefetch route, or the active route for mounted page actions.
     * @returns {Function} - Whether the operation still belongs to the current navigation.
     */
    function captureNavigation(route = activeRoute) {
        return () => activeRoute === undefined || activeRoute === route;
    }

    function raise(code) {
        statusCode.value = code;
    }

    function clear() {
        statusCode.value = null;
    }
}, {
    shouldSkipReset: true
});
